import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import ErrorRounded from "@mui/icons-material/ErrorRounded";
import SaveRounded from "@mui/icons-material/SaveRounded";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDataGridSettings } from "@/components/DataGridSettingsProvider";
import { useViewContext } from "@/components/ViewProvider";
import { useSaveViewOverride } from "@/hooks/views/useSaveViewOverride";
import { ViewSettings } from "@/models/DiagnosticView";

export function SaveViewOverrideButton() {
  const { t } = useTranslation();
  const dataGrid = useDataGridSettings();
  const { apiRef, pageType } = dataGrid;
  const { selectedView } = useViewContext();
  const { saveOverride } = useSaveViewOverride(pageType);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const renderIcon = () => {
    if (status === "loading") return <CircularProgress size={20} />;
    if (status === "success") return <CheckCircleRounded color="success" />;
    if (status === "error") return <ErrorRounded color="error" />;
    return <SaveRounded fontSize="inherit" />;
  };

  const buildSettingsDelta = (): Partial<ViewSettings> | undefined => {
    if (!selectedView) return undefined;
    const base = selectedView.settings;
    const candidate: Partial<ViewSettings> = {};
    if (dataGrid.hideOldData !== base.hideOldData) candidate.hideOldData = dataGrid.hideOldData;
    if (dataGrid.includeDismissedWarnings !== base.includeDismissedWarnings)
      candidate.includeDismissedWarnings = dataGrid.includeDismissedWarnings;
    if (dataGrid.deviceCountLimit !== base.deviceCountLimit)
      candidate.deviceCountLimit = dataGrid.deviceCountLimit;
    if (dataGrid.loadAddresses !== base.loadAddresses)
      candidate.loadAddresses = dataGrid.loadAddresses;
    if (dataGrid.chargeRateOnlyWhenCharging !== base.chargeRateOnlyWhenCharging)
      candidate.chargeRateOnlyWhenCharging = dataGrid.chargeRateOnlyWhenCharging;
    if (dataGrid.showArchivedVehicles !== base.showArchivedVehicles)
      candidate.showArchivedVehicles = dataGrid.showArchivedVehicles;
    if (dataGrid.alertDisplayMode !== base.alertDisplayMode)
      candidate.alertDisplayMode = dataGrid.alertDisplayMode;
    if (dataGrid.alertAutoDismiss !== base.alertAutoDismiss)
      candidate.alertAutoDismiss = dataGrid.alertAutoDismiss;
    if (JSON.stringify(dataGrid.precisions) !== JSON.stringify(base.precisions))
      candidate.precisions = dataGrid.precisions;
    if (JSON.stringify(dataGrid.colorIntervals) !== JSON.stringify(base.colorIntervals))
      candidate.colorIntervals = dataGrid.colorIntervals;
    if (JSON.stringify(dataGrid.columnNames) !== JSON.stringify(base.columnNames))
      candidate.columnNames = dataGrid.columnNames;
    if (JSON.stringify(dataGrid.alertRules) !== JSON.stringify(base.alertRules))
      candidate.alertRules = dataGrid.alertRules;
    return Object.keys(candidate).length > 0 ? candidate : undefined;
  };

  const onClick = async () => {
    if (!selectedView || !apiRef.current?.exportState) return;
    setStatus("loading");
    const state = apiRef.current.exportState();
    if (state?.pagination) {
      state.pagination.rowCount = undefined;
      state.pagination.paginationModel = undefined;
    }
    const delta = buildSettingsDelta();
    console.warn("[SaveOverride] click", {
      viewId: selectedView.id,
      currentDeviceCountLimit: dataGrid.deviceCountLimit,
      baseDeviceCountLimit: selectedView.settings.deviceCountLimit,
      delta,
    });
    const ok = await saveOverride(selectedView, JSON.stringify(state), delta);
    setStatus(ok ? "success" : "error");
    setTimeout(() => setStatus("idle"), 3000);
  };

  return (
    <Button color="primary" size="small" startIcon={renderIcon()} onClick={onClick} disabled={!selectedView}>
      {t("views.save")}
    </Button>
  );
}
